import { useQuery } from '@tanstack/react-query';
import { templatesApi, orgTemplatesApi, ApiError } from '../services/api';
import { trackingRetry } from './useTracking';
import type { TemplateSource } from '../types';

export function useTemplates(params?: { activeOnly?: boolean; limit?: number }) {
  return useQuery({
    queryKey: ['templates', params],
    queryFn: () => templatesApi.list(params),
  });
}

export function useTemplate(id: string) {
  return useQuery({
    queryKey: ['templates', id],
    queryFn: () => templatesApi.get(id),
    enabled: !!id,
  });
}

export function useTemplateWithSchema(id: string) {
  return useQuery({
    queryKey: ['templates', id, 'schema'],
    queryFn: () => templatesApi.getWithSchema(id),
    enabled: !!id,
  });
}

/**
 * Hook to get presigned URL for downloading template reference PDF.
 * Handles both global and organisation templates based on source.
 *
 * @param templateId - Template ID
 * @param source - 'global' or 'organisation'
 * @param enabled - Whether to enable the query (default: true)
 * @returns Query result with PDF URL or null if no PDF configured
 */
export function useTemplatePdfUrl(
  templateId: string,
  source: TemplateSource,
  enabled = true
) {
  return useQuery({
    queryKey: ['template-pdf-url', templateId, source],
    queryFn: async () => {
      try {
        const result =
          source === 'global'
            ? await templatesApi.getReferencePdfUrl(templateId)
            : await orgTemplatesApi.getReferencePdfUrl(templateId);
        return result;
      } catch (error) {
        // 404 means no PDF configured - return null instead of throwing
        if (error instanceof ApiError && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!templateId && enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes (URL valid for 1 hour)
    gcTime: 35 * 60 * 1000, // 35 minutes
    retry: trackingRetry,
  });
}
